import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import useCurrentContext from '~/common/hooks/useCurrentContext';
import {
  clearHeapEventProperties,
  setHeapEventProperties,
} from '~/common/utils/analytics/heap.utils';
import { selectActingRole } from '~/store/profile/profileSlice.selectors';

const ACCESS_SCOPES = {
  ORG: 'org',
  HUB: 'hub',
  FLEET: 'fleet',
} as const;

/**
 * Keeps the acting role and the selected organization, hub, and fleet on every
 * Heap event, so each event can be reported per user and per fleet. With no
 * organization selected (logged out, or after leaving view-as), the properties
 * are cleared.
 */
const useHeapEventContext = (): void => {
  const actingRole = useSelector(selectActingRole);
  const currentContext = useCurrentContext();
  const organizationsId = currentContext?.organizationsId;
  const hubsId = currentContext?.hubsId;
  const fleetsId = currentContext?.fleetsId;

  useEffect(() => {
    if (!organizationsId) {
      clearHeapEventProperties();
      return;
    }

    const accessScope = fleetsId
      ? ACCESS_SCOPES.FLEET
      : hubsId
        ? ACCESS_SCOPES.HUB
        : ACCESS_SCOPES.ORG;

    setHeapEventProperties({
      actor_role: actingRole ?? 'unknown',
      org_id: organizationsId,
      hub_id: hubsId ?? null,
      fleet_id: fleetsId ?? null,
      access_scope: accessScope,
    });
  }, [actingRole, organizationsId, hubsId, fleetsId]);
};

export default useHeapEventContext;
